// Import Quill styles for in-place editing
import "react-quill/dist/quill.snow.css";

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Typography,
} from "@mui/material";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import {
  ArtifactService,
  useArtifactContent,
  useArtifactProvenance,
} from "../../services/ArtifactService";

import EditIcon from "@mui/icons-material/Edit";
import ExpandIcon from "@mui/icons-material/Fullscreen";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import SaveIcon from "@mui/icons-material/Save";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Markdown from "react-markdown";
import ReactQuill from "react-quill";
import { toast } from "react-toastify";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import type { Pluggable } from "unified";
import ArtifactFullViewer from "./ArtifactFullViewer";
import GeneratedDocProvenance from "./GeneratedDocProvenance";
import ArtifactCitationLink from "./ArtifactCitationLink";
import { rehypeWrapCite } from "src/views/assistant/rehype/rehypeWrapCite";
import type { SourceProvenanceReference } from "../../services/ArtifactService";

// Sanitization schema for artifact markdown: the default allowlist (which
// already permits standard formatting, tables, and <cite>) with explicit
// affirmation of cite so provenance markers can never be stripped by an
// upstream schema change.
const ARTIFACT_SANITIZE_SCHEMA = {
  ...defaultSchema,
  tagNames: [...(defaultSchema.tagNames ?? []), "cite"],
};

// Helper functions for markdown/HTML conversion
const markdownToHtml = (markdown: string): string => {
  // Simple markdown to HTML conversion for common elements
  let html = markdown
    // Headers
    .replace(/^### (.*$)/gim, "<h3>$1</h3>")
    .replace(/^## (.*$)/gim, "<h2>$1</h2>")
    .replace(/^# (.*$)/gim, "<h1>$1</h1>")
    // Bold
    .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
    .replace(/__(.*?)__/g, "<strong>$1</strong>")
    // Italic
    .replace(/\*(.*?)\*/g, "<em>$1</em>")
    .replace(/_(.*?)_/g, "<em>$1</em>")
    // Code blocks
    .replace(/```([\s\S]*?)```/g, "<pre><code>$1</code></pre>")
    .replace(/`(.*?)`/g, "<code>$1</code>")
    // Links
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>')
    // Lists
    .replace(/^\* (.*$)/gim, "<li>$1</li>")
    .replace(/^- (.*$)/gim, "<li>$1</li>")
    .replace(/^\d+\. (.*$)/gim, "<li>$1</li>")
    // Line breaks
    .replace(/\n\n/g, "</p><p>")
    .replace(/\n/g, "<br>");

  // Wrap in paragraphs and handle lists
  html = "<p>" + html + "</p>";
  html = html.replace(/<p><\/p>/g, "");
  html = html.replace(/(<li>.*<\/li>)/gs, "<ul>$1</ul>");

  return html;
};

const htmlToMarkdown = (html: string): string => {
  // Simple HTML to markdown conversion
  let markdown = html
    // Headers
    .replace(/<h1>(.*?)<\/h1>/g, "# $1\n\n")
    .replace(/<h2>(.*?)<\/h2>/g, "## $1\n\n")
    .replace(/<h3>(.*?)<\/h3>/g, "### $1\n\n")
    .replace(/<h4>(.*?)<\/h4>/g, "#### $1\n\n")
    .replace(/<h5>(.*?)<\/h5>/g, "##### $1\n\n")
    .replace(/<h6>(.*?)<\/h6>/g, "###### $1\n\n")
    // Bold and italic
    .replace(/<strong>(.*?)<\/strong>/g, "**$1**")
    .replace(/<b>(.*?)<\/b>/g, "**$1**")
    .replace(/<em>(.*?)<\/em>/g, "*$1*")
    .replace(/<i>(.*?)<\/i>/g, "*$1*")
    // Code
    .replace(/<pre><code>([\s\S]*?)<\/code><\/pre>/g, "```\n$1\n```\n\n")
    .replace(/<code>(.*?)<\/code>/g, "`$1`")
    // Links
    .replace(/<a href="([^"]*)"[^>]*>(.*?)<\/a>/g, "[$2]($1)")
    // Lists
    .replace(/<ul><li>(.*?)<\/li><\/ul>/gs, (_, content) => {
      return content
        .replace(/<\/li><li>/g, "\n- ")
        .replace(/^/, "- ")
        .replace(/$/, "\n\n");
    })
    .replace(/<ol><li>(.*?)<\/li><\/ol>/gs, (_, content) => {
      let counter = 1;
      return content
        .replace(/<\/li><li>/g, () => `\n${++counter}. `)
        .replace(/^/, "1. ")
        .replace(/$/, "\n\n");
    })
    // Paragraphs and breaks
    .replace(/<\/p><p>/g, "\n\n")
    .replace(/<p>/g, "")
    .replace(/<\/p>/g, "\n\n")
    .replace(/<br\s*\/?>/g, "\n")
    // Clean up extra whitespace
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  return markdown;
};

interface ArtifactDisplayProps {
  artifactId: string | number;
  artifactName?: string;
  message?: string;
}

const ArtifactDisplay: React.FC<ArtifactDisplayProps> = memo(
  ({ artifactId, artifactName, message }) => {
    const [isFullViewOpen, setIsFullViewOpen] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [quillContent, setQuillContent] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const printRef = useRef<HTMLDivElement>(null);

    const {
      data: content,
      isLoading,
      error,
      refetch,
    } = useArtifactContent(artifactId);

    // Source-provenance manifest for the generated document (#20423 Story 5).
    const { data: provenance } = useArtifactProvenance(artifactId);

    // Inline-citation wiring for the generated-doc body (#20423 Story 5). The
    // stored content carries the same <cite>{chunk_id}</cite> tags chat uses;
    // the manifest maps each chunk_id to its reference number `n` and source.
    // We reuse the chat rehype pipeline (rehypeRaw folds the raw <cite> into an
    // element; rehypeWrapCite numbers it) and render a clickable [n] that opens
    // the source — resolving via the manifest, not chat responseSources.
    const { citeNumbers, refsByChunk, references } = useMemo(() => {
      const refs: SourceProvenanceReference[] = provenance?.references ?? [];
      const numbers = new Map<number, number>();
      const byChunk = new Map<number, SourceProvenanceReference>();
      for (const r of refs) {
        if (r.chunk_id != null) {
          numbers.set(r.chunk_id, r.n);
          byChunk.set(r.chunk_id, r);
        }
      }
      return { citeNumbers: numbers, refsByChunk: byChunk, references: refs };
    }, [provenance]);

    const markdownRehypePlugins: Pluggable[] = useMemo(
      () =>
        citeNumbers.size > 0
          ? [
              // rehypeRaw parses raw inline HTML (needed to fold <cite> tags
              // into elements) — artifact content is model-generated AND
              // user-editable, and artifacts can be shared, so sanitize
              // between raw-parse and cite-numbering: <cite> survives (it's
              // in the allowlist), scripts/event handlers/iframes don't.
              rehypeRaw,
              [rehypeSanitize, ARTIFACT_SANITIZE_SCHEMA],
              [rehypeWrapCite, { citationNumbers: citeNumbers }],
            ]
          : [],
      [citeNumbers],
    );

    // Initialize Quill content when data loads
    useEffect(() => {
      if (content && !isLoading && !error) {
        // Check if content is markdown and convert to HTML for Quill
        if (
          artifactName?.endsWith(".md") ||
          /\[.*\]\(.*\)/.test(content) || // Check for markdown links
          content.includes("# ") ||
          content.includes("## ") ||
          content.includes("**") ||
          content.includes("*") ||
          content.includes("`")
        ) {
          // Convert markdown to HTML for Quill display
          setQuillContent(markdownToHtml(content));
        } else if (content.trim().startsWith("<")) {
          // Already HTML
          setQuillContent(content);
        } else {
          // Plain text - wrap in paragraphs
          setQuillContent(
            `<p>${content
              .replace(/\n\n/g, "</p><p>")
              .replace(/\n/g, "<br>")}</p>`,
          );
        }
      }
    }, [content, isLoading, error, artifactName]);

    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

    const handleSaveAsPdf = async () => {
      setIsGeneratingPdf(true);
      try {
        // Use backend Gotenberg-based PDF generation for proper formatting
        await ArtifactService.downloadArtifactAsPdf(
          artifactId,
          artifactName || `Artifact_${artifactId}`,
        );
        toast.success("PDF downloaded successfully!");
      } catch (error) {
        console.error("PDF generation failed:", error);
        toast.error("Failed to generate PDF");
      } finally {
        setIsGeneratingPdf(false);
      }
    };

    const handleOpenArtifact = () => {
      setIsFullViewOpen(true);
    };

    const handleCloseFullView = () => {
      setIsFullViewOpen(false);
      refetch(); // Refresh content when closing in case it was edited
    };

    const handleToggleEditMode = () => {
      setIsEditMode(!isEditMode);
    };

    const handleQuillChange = (content: string) => {
      setQuillContent(content);
    };

    const handleSave = async () => {
      if (!quillContent.trim()) {
        toast.error("Content cannot be empty");
        return;
      }

      setIsSaving(true);
      try {
        let contentToSave = quillContent;

        // If the original content was markdown, convert back to markdown
        if (
          artifactName?.endsWith(".md") ||
          (content &&
            (content.includes("# ") ||
              content.includes("## ") ||
              content.includes("**") ||
              content.includes("*") ||
              content.includes("`")))
        ) {
          contentToSave = htmlToMarkdown(quillContent);
        }

        await ArtifactService.updateArtifactContent(artifactId, contentToSave);
        toast.success("Artifact updated successfully!");
        refetch(); // Refresh the content
        setIsEditMode(false); // Exit edit mode after saving
      } catch (error) {
        toast.error("Failed to save changes");
        console.error("Failed to save artifact:", error);
      } finally {
        setIsSaving(false);
      }
    };

    // Quill configuration for in-place editing
    const quillModules = {
      toolbar: [
        [{ header: [1, 2, 3, false] }],
        ["bold", "italic", "underline", "strike"],
        [{ list: "ordered" }, { list: "bullet" }],
        ["link"],
        ["clean"],
      ],
    };

    const quillFormats = [
      "header",
      "bold",
      "italic",
      "underline",
      "strike",
      "list",
      "bullet",
      "link",
    ];

    if (isLoading) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
        >
          <CircularProgress />
          <Typography variant="body2" sx={{ ml: 2 }}>
            Loading artifact content...
          </Typography>
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load artifact content: {(error as Error).message}
        </Alert>
      );
    }

    return (
      <Box>
        {/* Header with artifact info and controls */}
        <Box
          mb={2}
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Box>
            <Typography variant="h6" component="div" gutterBottom>
              {artifactName || `Artifact ${artifactId}`}
            </Typography>
            {message && (
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {message}
              </Typography>
            )}
          </Box>

          {/* Edit controls */}
          <Box display="flex" gap={1}>
            <IconButton
              size="small"
              onClick={handleToggleEditMode}
              color={isEditMode ? "primary" : "default"}
              title={isEditMode ? "View Mode" : "Edit Mode"}
            >
              {isEditMode ? <VisibilityIcon /> : <EditIcon />}
            </IconButton>
            <IconButton
              size="small"
              onClick={handleOpenArtifact}
              title="Full Screen Edit"
            >
              <ExpandIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Content display area */}
        <Box
          ref={printRef}
          sx={{
            p: 3,
            bgcolor: "background.paper",
            borderRadius: 1,
            border: "1px solid",
            borderColor: "divider",
            minHeight: "400px",
            maxHeight: "60vh",
            overflow: "auto",
          }}
        >
          {isEditMode ? (
            // Edit mode with Quill
            <Box
              sx={{
                height: "100%",
                minHeight: "350px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <ReactQuill
                value={quillContent}
                onChange={handleQuillChange}
                modules={quillModules}
                formats={quillFormats}
                theme="snow"
                style={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
                placeholder="Edit your content here..."
              />
            </Box>
          ) : // View mode
          content ? (
            content.includes("<!DOCTYPE html>") || content.includes("<html") ? (
              // If content is the main app HTML (API endpoint not working)
              <Alert severity="warning" sx={{ m: 2 }}>
                <Typography variant="body2" gutterBottom>
                  <strong>API Endpoint Issue Detected</strong>
                </Typography>
                <Typography variant="body2" gutterBottom>
                  The artifact content API endpoint `/artifacts/{artifactId}
                  /content` is not working correctly and is returning the main
                  application HTML instead of the artifact content.
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Artifact ID:</strong> {artifactId}
                </Typography>
                <Typography variant="body2">
                  Please check the backend API configuration for the artifacts
                  endpoint.
                </Typography>
              </Alert>
            ) : content.trim().startsWith("<") ? (
              // If content starts with '<' but is not the main app HTML, treat it as artifact HTML
              <div
                dangerouslySetInnerHTML={{ __html: content }}
                style={{
                  fontSize: "14px",
                  lineHeight: 1.5,
                }}
              />
            ) : artifactName?.endsWith(".md") ||
              content.includes("# ") ||
              content.includes("## ") ||
              content.includes("<cite>") ||
              /\[.*\]\(.*\)/.test(content) ? (
              // If it's a markdown file or contains markdown, render as markdown
              <Markdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={markdownRehypePlugins}
                components={{
                  // Inline [n] citation markers in the generated-doc body,
                  // clickable back to the source passage (#20423 Story 5).
                  cite: (props: any) => {
                    // react-markdown v9 exposes rehype-set properties on
                    // node.properties, not as top-level props (same access
                    // pattern as AAAssistantText).
                    const properties = props.node?.properties ?? {};
                    const rawNumber = properties["citationNumber"];
                    return (
                      <ArtifactCitationLink
                        citationNumber={
                          rawNumber == null ? null : Number(rawNumber)
                        }
                        chunkId={properties["citationData"]?.chunk_id ?? null}
                        refsByChunk={refsByChunk}
                        references={references}
                      />
                    );
                  },
                  h1: (props: any) => (
                    <Typography variant="h4" component="h1" gutterBottom>
                      {props.children}
                    </Typography>
                  ),
                  h2: (props: any) => (
                    <Typography variant="h5" component="h2" gutterBottom>
                      {props.children}
                    </Typography>
                  ),
                  h3: (props: any) => (
                    <Typography variant="h6" component="h3" gutterBottom>
                      {props.children}
                    </Typography>
                  ),
                  p: (props: any) => (
                    <Typography variant="body1" paragraph>
                      {props.children}
                    </Typography>
                  ),
                  a: (props: any) => (
                    <a
                      href={props.href}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {props.children}
                    </a>
                  ),
                }}
              >
                {content}
              </Markdown>
            ) : (
              // Otherwise, display as preformatted text
              <Box
                sx={{
                  fontFamily: "monospace",
                  fontSize: "14px",
                  lineHeight: 1.5,
                  whiteSpace: "pre-wrap",
                }}
              >
                {content}
              </Box>
            )
          ) : (
            "Loading artifact content..."
          )}
        </Box>

        {/* Source provenance summary + clickable "FROM [n]" references */}
        {!isEditMode && provenance && (
          <GeneratedDocProvenance provenance={provenance} />
        )}

        {/* Action buttons */}
        <Box display="flex" gap={1} mt={2} justifyContent="space-between">
          <Box display="flex" gap={1}>
            {isEditMode && (
              <Button
                variant="contained"
                size="small"
                onClick={handleSave}
                disabled={isSaving}
                startIcon={
                  isSaving ? <CircularProgress size={16} /> : <SaveIcon />
                }
              >
                {isSaving ? "Saving..." : "Save"}
              </Button>
            )}
            <Button
              variant="outlined"
              size="small"
              onClick={handleSaveAsPdf}
              disabled={isGeneratingPdf}
              startIcon={
                isGeneratingPdf ? (
                  <CircularProgress size={16} />
                ) : (
                  <PictureAsPdfIcon />
                )
              }
            >
              {isGeneratingPdf ? "Generating PDF..." : "Save as PDF"}
            </Button>
          </Box>
        </Box>

        {/* Full-screen viewer */}
        <ArtifactFullViewer
          open={isFullViewOpen}
          onClose={handleCloseFullView}
          artifactId={artifactId}
          artifactName={artifactName}
        />
      </Box>
    );
  },
);

ArtifactDisplay.displayName = "ArtifactDisplay";

export default ArtifactDisplay;
